import {
  Component,
  inject,
  input,
  OnChanges,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AMENDMENT_DIALOG_CONFIG,
  AmendmentDialog,
  AmendmentDialogData,
} from '@app/features/amendments/amendment-dialog/amendment-dialog';
import { AmendmentEligibility } from '@app/features/amendments/amendment.model';
import { AmendmentService } from '@app/features/amendments/amendment.service';
import { Contract } from '@app/features/contracts/contract.model';
import { LucideFilePenLine } from '@lucide/angular';
import { RxjsDirective } from '@app/shared/rxjs.directive';

/**
 * L'action « Établir un avenant », proposée seulement quand l'API dit que c'est possible : un CDI
 * ou un CDD A dont le terme n'est pas passé, signé ou pas encore, sans autre avenant en cours.
 * Sinon, elle dit pourquoi. Un contrat annulé ou supprimé ne la montre pas.
 */
@Component({
  selector: 'app-button-establish-amendment',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, LucideFilePenLine],
  templateUrl: './button-establish-amendment.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./button-establish-amendment.scss'],
})
export class ButtonEstablishAmendment extends RxjsDirective implements OnChanges {
  readonly contract = input.required<Contract>();

  private readonly amendmentService = inject(AmendmentService);
  private readonly dialog = inject(MatDialog);

  readonly eligibility = signal<AmendmentEligibility | null>(null);

  ngOnChanges(): void {
    this.refresh();
  }

  refresh(): void {
    const contractId = this.contract().id;
    if (!contractId || this.contract().status === 'CANCELED' || this.contract().deleted) {
      this.eligibility.set(null);
      return;
    }
    this.register(
      this.amendmentService
        .eligibility(contractId)
        .subscribe((eligibility) => this.eligibility.set(eligibility)),
    );
  }

  establish(): void {
    const dialogRef = this.dialog.open<AmendmentDialog, AmendmentDialogData>(AmendmentDialog, {
      data: { contract: this.contract() },
      ...AMENDMENT_DIALOG_CONFIG,
    });
    this.register(dialogRef.afterClosed().subscribe(() => this.refresh()));
  }
}
